// Pure rules and constants of the document's video player. No React and no DOM,
// so the inline player, the static body and the phone viewer share one set of
// rules and each can be checked on its own.
import { normalizeFileWidth } from "@trameria/editor";

/** Speed steps (YouTube's list without 0.25 and 1.75). */
export const VIDEO_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;
/** Seconds skipped by ArrowLeft / ArrowRight. */
export const VIDEO_SEEK_SHORT = 5;
/** Seconds skipped by J / L. */
export const VIDEO_SEEK_LONG = 10;
/**
 * Height cap (px) of a video without a set width. The same 420 as images in the
 * body, so a portrait video does not fill the body.
 */
export const VIDEO_MAX_HEIGHT = 420;
/**
 * Wait before the one retry after a load error. Same reason as images: right
 * after an upload the file answers 404 for a few seconds.
 */
export const VIDEO_RETRY_MS = 3000;
/** How long the mouse rests before the controls hide. */
export const VIDEO_IDLE_MS = 2000;

/**
 * The outer box of a video in the body. It anchors the "…" and the resize
 * handles of the file block (`.trameria-file-box`), and the node view's
 * `stopEvent` keeps everything inside it away from ProseMirror. Its width is set
 * by `videoOuterStyle`. **The static body draws the same box**: if the boxes
 * differ, the body jumps when the view switches to the editor after sync.
 */
export const VIDEO_BOX_CLASS = "trameria-file-box relative max-w-full";

export function formatVideoTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const min = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(min).padStart(2, "0")}:${ss}` : `${min}:${ss}`;
}

/** One step from the speed step nearest to `current` (stops at the ends). */
export function stepSpeed(current: number, dir: 1 | -1): number {
  let at = 0;
  VIDEO_SPEEDS.forEach((v, i) => {
    if (Math.abs(v - current) < Math.abs(VIDEO_SPEEDS[at] - current)) at = i;
  });
  const next = Math.min(Math.max(at + dir, 0), VIDEO_SPEEDS.length - 1);
  return VIDEO_SPEEDS[next];
}

/** Keeps a time within 0..duration. Unknown duration (not loaded, live): only stops below 0. */
export function clampTime(t: number, duration: number): number {
  if (!Number.isFinite(duration) || duration <= 0) return Math.max(0, t);
  return Math.min(Math.max(0, t), duration);
}

/** Where on the seek bar was pressed, as 0..1. */
export function seekRatio(
  clientX: number,
  left: number,
  width: number,
): number {
  if (width <= 0) return 0;
  return Math.min(Math.max((clientX - left) / width, 0), 1);
}

export type VideoKeyAction =
  | { type: "toggle" }
  | { type: "seek"; delta: number }
  | { type: "fullscreen" }
  | { type: "mute" }
  | { type: "speed"; dir: 1 | -1 };

/**
 * Keys while the player has focus (the same as YouTube). Keys with a modifier
 * are left alone, so app shortcuts like ⌘K are never taken. The caller
 * `preventDefault`s (and stops) the keys it takes.
 */
export function videoKeyAction(e: {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
}): VideoKeyAction | null {
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  switch (e.key) {
    case " ":
    case "k":
    case "K":
      return { type: "toggle" };
    case "j":
    case "J":
      return { type: "seek", delta: -VIDEO_SEEK_LONG };
    case "l":
    case "L":
      return { type: "seek", delta: VIDEO_SEEK_LONG };
    case "ArrowLeft":
      return { type: "seek", delta: -VIDEO_SEEK_SHORT };
    case "ArrowRight":
      return { type: "seek", delta: VIDEO_SEEK_SHORT };
    case "f":
    case "F":
      return { type: "fullscreen" };
    case "m":
    case "M":
      return { type: "mute" };
    case "<":
      return { type: "speed", dir: -1 };
    case ">":
      return { type: "speed", dir: 1 };
    default:
      return null;
  }
}

/** Width of the outer box: the column's width when unset. Rounding is `normalizeFileWidth` alone. */
export function videoOuterStyle(width: number | null): { width: string } {
  const px = normalizeFileWidth(width);
  return { width: px === null ? "100%" : `${px}px` };
}

/**
 * The player's frame. Aspect ratio is the loaded video's (16:9 until known).
 * Only without a set width is the height capped at {@link VIDEO_MAX_HEIGHT}
 * (like images: a portrait video does not fill the body); the room left over
 * is the black ground with the video `object-contain`ed.
 */
export function videoFrameStyle(
  width: number | null,
  ratio: number | null,
): { aspectRatio: string; maxHeight?: string } {
  const aspectRatio = String(
    ratio !== null && Number.isFinite(ratio) && ratio > 0 ? ratio : 16 / 9,
  );
  return normalizeFileWidth(width) === null
    ? { aspectRatio, maxHeight: `${VIDEO_MAX_HEIGHT}px` }
    : { aspectRatio };
}
